import { checkoutPayFlowController } from '@cart-services/core/checkoutPayFlowController'
import * as paymentService from '@cart-services/core/paymentService'
import { type ApplePayDialogContext } from '@cart-services/hooks/useApplePayDialogActions'
import { type CountryCode } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiFpPaymentExecution } from '@hooks-api/fortune/useApiFpPaymentExecution'
import { useApiGetFpPayment } from '@hooks-api/fortune/useApiGetFpPayment'
import { ECommerceOrderType } from '@hooks-api/types/dataModel/eCommercePayments'
import { type BaseCheckoutPayFlowModule } from '@payments/lib/checkout-pay/BaseCheckoutPayFlowModule'
import { useRef } from 'react'

export interface UseFpCampaignApplePayDialogFlowParams {
  paymentModule: BaseCheckoutPayFlowModule
  countryCode: CountryCode
  /** 確定要整頁導頁前呼叫，供 caller 切換全頁 loading（與信用卡 dialog 同形狀）。 */
  onRedirecting: () => void
}

/**
 * Campaign CKO Flow Apple Pay dialog flow hook。
 * 訂單於進 dialog 前已由 caller 建好，init 以真交易單 id（TRANSACTION）取得 session。
 */
export const useFpCampaignApplePayDialogFlow = ({
  paymentModule,
  countryCode,
  onRedirecting,
}: UseFpCampaignApplePayDialogFlowParams) => {
  const { trigger: getPayment } = useApiGetFpPayment()
  const { trigger: executePayment } = useApiFpPaymentExecution()

  const activeCkoMountRef = useRef<paymentService.CkoMount | null>(null)

  const mountApplePayButton = useCallbackEvent(async (context: ApplePayDialogContext, containerId: string) => {
    const initResult = await getPayment({
      order_type: ECommerceOrderType.TRANSACTION,
      order_id: context.paymentTransactionId,
      payment_method_id: context.paymentOptionId,
      remember_payment_card_token: false,
      installment: null,
      payment_success_url: context.paymentSuccessUrl,
      payment_fallback_url: context.paymentFallbackUrl,
    })
    const paymentInfo = initResult?.data?.data
    if (!paymentInfo) {
      throw new Error('[useFpCampaignApplePayDialogFlow] getPayment returned empty paymentInfo')
    }
    activeCkoMountRef.current = await paymentService.applyCkoSession(paymentModule, paymentInfo, paymentModule.getAppEnv(), containerId, { countryCode })
  })

  const awaitAndExecutePayment = useCallbackEvent(async (context: ApplePayDialogContext) => {
    const ckoPaymentId = await checkoutPayFlowController.awaitPaymentId({
      paymentTransactionId: context.paymentTransactionId,
      orderHash: context.orderHash,
    })
    datadogRum.addAction('applePayDialog.paymentCompleted', {
      paymentTransactionId: context.paymentTransactionId,
      ckoPaymentId,
    })
    const { data: paymentResult } = await paymentService.executeWithCkoReconciliationRetry(() => executePayment({
      payment_transaction_id: context.paymentTransactionId,
      service_pay_info: { bank_code: null, apple_pay_info: null, pay_token: null },
      user_payment_token_info: null,
      payment_success_url: context.paymentSuccessUrl,
      payment_fallback_url: context.paymentFallbackUrl,
      payment_option_id: context.paymentOptionId,
    }))
    if (!paymentResult.redirect_url) {
      throw new Error('[useFpCampaignApplePayDialogFlow] executePayment returned no redirect_url')
    }
    onRedirecting()
    window.location.href = paymentResult.redirect_url
  })

  const cancelInFlight = useCallbackEvent((reason: string) => {
    checkoutPayFlowController.cancelInFlight(reason)
    activeCkoMountRef.current?.unmount()
    activeCkoMountRef.current = null
  })

  return {
    mountApplePayButton,
    awaitAndExecutePayment,
    cancelInFlight,
  }
}
